import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import React from 'react'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Contained } from '../components/Contained'
import { Content } from '../components/Content'
import { HelpSearchBox } from '../components/help/HelpSearchBox'
import { QuestionList } from '../components/help/QuestionList'
import { HelpSubtitle } from '../components/HelpSubtitle'
import { Intro } from '../components/Intro'
import { LightTabs } from '../components/LightTabs'
import { MissingContent } from '../components/MissingContent'
import { RadioButtons } from '../components/RadioButtons'
import { SearchResultsTitle } from '../components/SearchResultsTitle'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { WidthBound } from '../components/WidthBound'
import { usePageLink } from '../src/hooks/usePageLink'
import { useTranslate } from '../src/hooks/useTranslate'
import {
	HelpQuestionFragment,
	PageFragment,
	SpecialPageType,
	TopicFragment,
	UUID,
} from '../src/model/fragments'
import { getLocale, getPossibleLocales, Locale } from '../src/model/locale'
import { getLinkWithQuery } from '../src/model/urls'
import { fetchGet } from '../src/utils/fetchWrapper'



export default function Help({
	page,
	possibleLocales,
	topics,
	topicId,
	searchQuery,
	questions,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {

	const router = useRouter();
	const translate = useTranslate();

	const helpLink = usePageLink(SpecialPageType.help)!
	const makeLink = React.useCallback((linkTopic: {id: UUID} | null, q: string | null) => {

		const link = {
			...getLinkWithQuery(helpLink, q ? { q: q!} : {
				topic: linkTopic?.id,
			}),
			scroll: false,
		};

		return link;
	}, [helpLink]);	
	
	const data = page ? getLocale(page.locales, possibleLocales) : null;
	if (!page || !data) {
		return <MissingContent />
	}

	return (
		<>
			<Seo seo={data.seo} />
			<Breadcrumbs
				items={[
					{
						label: data.title,
					},
				]}
			/>

			<WidthBound>
				<Intro title={data.title}>
						<Content content={data.content} innerSize="narrow" />
				</Intro>
			</WidthBound>
			<WidthBound>
				<LightTabs
					items={[]}
					aside={<HelpSearchBox currentQuery={searchQuery} onSearch={q => {

						const link = makeLink(null, q);
						router.push(link.href, link.as, {shallow: false});
					}} />}
				/>
			</WidthBound>
			<Contained>
				<Spacer initial={20} />
				{!searchQuery && (
					<WidthBound>
						<RadioButtons
							items={[
								{
									text: translate('all-queries'),
									linkProps: helpLink,
								},
								...topics.map((topic) => ({
									text: getLocale(topic.locales, possibleLocales)?.title,
									linkProps: makeLink(topic, null),
								})),
							]}
							initialIndex={
								topicId === null
									? 0
									: 1 + topics.findIndex((topic) => topic.id === topicId)
							}
						/>
					</WidthBound>
				)}

				<HelpSubtitle
					title={translate('all-queries')}
					askQuestion={translate('ask-question')}
				/>

				{
					searchQuery && <WidthBound>
						<SearchResultsTitle>{searchQuery}</SearchResultsTitle>
					</WidthBound>
				}

				<QuestionList questions={questions} />

			</Contained>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const locale = ctx.query.locale as Locale
	const possibleLocales = getPossibleLocales(locale)
	const queryParams = ctx.query

	const q =
		typeof queryParams.q === 'string' && queryParams.q
			? queryParams.q
			: null

	const topicId =
		typeof queryParams.topic === 'string' ? queryParams.topic : null

	const {
		getPage: page,
		listHelpTopic: topics,
		listHelpQuestion: questions,
	} = await fetchGet('/faq/get', new URLSearchParams({locale: locale, topicId: topicId?.toString() ?? '', navigationId: ctx.query.id?.toString() ?? '', q: q?.toString() ?? ''})) as
	{
		getPage: PageFragment,
		listHelpTopic: TopicFragment[],
		listHelpQuestion: HelpQuestionFragment[]
	};

	return {
		props: {
			page,
			possibleLocales,
			topics,
			topicId,
			questions,
			searchQuery: q
		},
	}
}
